<template>
	<div>
		<div class="d-flex align-items-center gap-2 mb-2">
			<span class="fw-semibold">
				{{ work.length === 1 ? "Work" : `${work.length} Work rows` }}
			</span>
			<button
				class="btn btn-sm btn-outline-secondary ms-auto"
				@click="emit('clear')"
			>
				<i class="bi bi-x-lg"></i>
			</button>
		</div>

		<div
			v-for="row in work"
			:key="row.origin + row.id"
			class="border-bottom py-1"
		>
			<div class="d-flex gap-2 align-items-baseline">
				<span class="badge" :class="originBadge(row.origin)">{{
					row.origin
				}}</span>
				<span class="fw-semibold text-truncate">{{ row.title }}</span>
			</div>
			<div class="small text-muted">
				{{ row.priority ?? "—" }}
				<span v-if="row.due_at"> · due {{ formatDate(row.due_at) }}</span>
				· {{ row.target_summary }}
			</div>
		</div>

		<div v-if="directOnly" class="small text-muted mt-2">
			Direct work is already an Assignment; place it on a Route from the
			Assignments tab.
		</div>

		<div v-else class="mt-2">
			<div class="row g-2">
				<div class="col-12">
					<label class="form-label small mb-1">Technician</label>
					<select
						v-model.number="technicianId"
						class="form-select form-select-sm"
					>
						<option :value="0" disabled>Select a technician…</option>
						<option v-for="user in users" :key="user.id" :value="user.id">
							{{ user.display_name || user.username }}
						</option>
					</select>
				</div>
				<div class="col-12">
					<label class="form-label small mb-1">Date</label>
					<input
						v-model="day"
						type="date"
						class="form-control form-control-sm"
					/>
				</div>
			</div>
			<div v-if="targetError" class="small text-danger mt-2">
				{{ targetError }}
			</div>
			<button
				class="btn btn-sm btn-primary mt-2"
				:disabled="saving || !technicianId || !day || !!targetError"
				@click="assign"
			>
				Create Route with {{ assignable.length }}
				{{ assignable.length === 1 ? "assignment" : "assignments" }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { cellToLatLng } from "h3-js";

import { apiClient } from "@/client";
import type {
	LeadDetail,
	OperationsWorkRow,
	User,
	WorkOrderDetail,
} from "@/type/api";

const props = defineProps<{ users: User[]; work: OperationsWorkRow[] }>();
const emit = defineEmits<{
	(e: "assigned"): void;
	(e: "clear"): void;
	(e: "notify", title: string, message: string): void;
}>();

interface Assignable {
	row: OperationsWorkRow;
	target: [number, number];
}

const technicianId = ref(0);
const day = ref(new Date().toISOString().slice(0, 10));
const saving = ref(false);
const targetError = ref("");
const assignable = ref<Assignable[]>([]);

const directOnly = computed(
	() =>
		props.work.length > 0 && props.work.every((row) => row.origin === "direct"),
);

watch(
	() => props.work,
	async (rows) => {
		assignable.value = [];
		targetError.value = "";
		if (rows.length === 1) technicianId.value = 0;
		const loaded: Assignable[] = [];
		for (const row of rows) {
			if (row.origin === "direct") continue;
			const cell =
				row.origin === "lead"
					? (await apiClient.JSONGet<LeadDetail>(`/api/lead/${row.id}`))
							.cells[0]
					: (
							await apiClient.JSONGet<WorkOrderDetail>(
								`/api/work-order/${row.id}`,
							)
						).cells[0];
			if (!cell) {
				targetError.value = `${row.origin} ${row.id} has no H3 footprint; author its target in the Assignment form.`;
				continue;
			}
			const [lat, lng] = cellToLatLng(cell);
			loaded.push({ row, target: [lng, lat] });
		}
		assignable.value = loaded;
	},
	{ immediate: true, deep: true },
);

async function assign() {
	saving.value = true;
	const start = new Date(day.value + "T08:00:00Z");
	const end = new Date(day.value + "T17:00:00Z");
	try {
		const assignments = assignable.value.map(({ row, target }) => {
			const spec: Record<string, unknown> = {
				kind: row.origin === "lead" ? "inspect" : "custom",
				target_geometry: JSON.stringify({
					type: "Point",
					coordinates: target,
				}),
				start_time: start.toISOString(),
				end_time: end.toISOString(),
			};
			if (row.origin === "lead") spec.lead_id = row.id;
			else {
				spec.work_order_id = row.id;
				spec.instructions = row.title;
			}
			return spec;
		});
		await apiClient.JSONPost("/api/route", {
			technician_id: technicianId.value,
			window_start: start.toISOString(),
			window_end: end.toISOString(),
			assignments,
		});
		emit("assigned");
	} catch (e: any) {
		emit("notify", "Work not assigned", e?.message ?? "The Route was refused.");
	} finally {
		saving.value = false;
	}
}

function originBadge(origin: string) {
	switch (origin) {
		case "lead":
			return "text-bg-primary";
		case "work_order":
			return "text-bg-warning";
		default:
			return "text-bg-secondary";
	}
}

function formatDate(value: string) {
	return new Date(value).toLocaleDateString();
}
</script>
